"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { QrCode, Share2, Download, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface QRShareButtonProps {
  /** The URL to encode in the QR code. Defaults to current page URL. */
  url?: string;
  /** Display title shown in the QR popup */
  title?: string;
  /** Optional subtitle / description */
  subtitle?: string;
  /** Thumbnail image shown inside the QR card */
  imageUrl?: string;
  variant?: "outline" | "ghost" | "default" | "secondary";
  size?: "sm" | "icon" | "default" | "lg";
  /** Show only the icon with no label text */
  iconOnly?: boolean;
  className?: string;
}

function buildQrUrl(data: string, px = 400): string {
  const encoded = encodeURIComponent(data);
  return `https://api.qrserver.com/v1/create-qr-code/?size=${px}x${px}&data=${encoded}&bgcolor=ffffff&color=000000&qzone=2&format=png`;
}

export function QRShareButton({
  url,
  title = "Share",
  subtitle,
  imageUrl,
  variant = "outline",
  size = "sm",
  iconOnly = true,
  className,
}: QRShareButtonProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const t = useTranslations("common");

  // Escape closes, like every other overlay.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Always use the canonical domain — never leak Vercel preview URLs
  const CANONICAL = process.env.NEXT_PUBLIC_APP_URL || "https://breakinbread.com";

  function canonicalise(raw: string): string {
    try {
      const u = new URL(raw);
      const canonical = new URL(CANONICAL);
      u.host = canonical.host;
      u.protocol = canonical.protocol;
      return u.toString();
    } catch {
      return raw;
    }
  }

  const resolvedUrl = canonicalise(
    url || (typeof window !== "undefined" ? window.location.href : CANONICAL)
  );

  const qrSrc = resolvedUrl ? buildQrUrl(resolvedUrl) : "";

  const handleShare = async () => {
    // Use native share sheet (iOS/Android) when available — includes copy as an option
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, url: resolvedUrl });
        return;
      } catch {
        // User dismissed or share failed — fall through to clipboard
      }
    }
    // Desktop fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(resolvedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: t("qrLinkCopied") });
    } catch {
      toast({ title: t("qrCopyFailed"), description: t("qrCopyManually"), variant: "destructive" });
    }
  };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = buildQrUrl(resolvedUrl, 600);
    a.download = `qr-${title.replace(/\s+/g, "-").toLowerCase()}.png`;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={cn("shrink-0", iconOnly && size === "sm" && "px-2.5", className)}
        aria-label={t("qrShow")}
        title={t("qrShow")}
      >
        <QrCode className="h-4 w-4" />
        {!iconOnly && <span className="ml-1.5">{t("qrCode")}</span>}
      </Button>

      {/* Full-screen, centred on the SCREEN: portalled to <body>, because a
          caller's bar with backdrop-blur (the news article bar) turns
          `fixed` into "relative to that bar" and the card hung off the top
          of the page (Gordon 2026-11-02). */}
      {open && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center overflow-y-auto bg-background"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          {/* Close button */}
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors"
            aria-label={t("close")}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex flex-col items-center gap-5 px-6 py-8 w-full max-w-xs text-center">

            {/* Title */}
            <div className="flex flex-col items-center gap-1.5 w-full">
              <h2 className="text-xl font-semibold leading-tight tracking-tight">
                {title}
              </h2>
              {subtitle && (
                <p className="text-sm text-muted-foreground">{subtitle}</p>
              )}
            </div>

            {/* QR card */}
            {qrSrc && (
              <div className="flex flex-col items-center rounded-2xl overflow-hidden shadow-2xl border border-border bg-white">
                {imageUrl && (
                  <div className="w-full flex items-center justify-center pt-5 pb-3 px-5">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageUrl}
                      alt={title}
                      className="w-20 h-20 rounded-xl object-cover shadow-md"
                    />
                  </div>
                )}
                <div className={cn("px-4", imageUrl ? "pb-4" : "py-4")}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={qrSrc}
                    alt="QR code"
                    width={240}
                    height={240}
                    className="block"
                    style={{ imageRendering: "pixelated" }}
                  />
                </div>
                <div className="w-full bg-black py-2 px-4 flex items-center justify-center gap-1.5">
                  <QrCode className="h-3 w-3 text-white/60" />
                  <span className="text-[10px] font-medium text-white/60 tracking-widest uppercase">
                    {t("qrScanToOpen")}
                  </span>
                </div>
              </div>
            )}

            {/* URL pill */}
            <p className="text-xs text-muted-foreground break-all max-w-[260px] leading-relaxed px-3 py-1.5 bg-muted rounded-lg">
              {resolvedUrl}
            </p>

            {/* Action buttons */}
            <div className="flex gap-3 w-full">
              <Button variant="outline" className="flex-1" onClick={handleShare}>
                {copied ? (
                  <Check className="h-4 w-4 mr-1.5 text-green-500" />
                ) : (
                  <Share2 className="h-4 w-4 mr-1.5" />
                )}
                {copied ? t("copied") : t("qrShareLink")}
              </Button>
              <Button variant="outline" className="flex-1" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-1.5" />
                {t("qrSave")}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
